import {
  QueryClient,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { ApiError, apiFetch } from './client';
import type { LlmRouting, Settings } from './contract';
import type {
  BreakoutsResponse,
  FreshnessResponse,
  GenreDetailResponse,
  GenreDimension,
  GenresResponse,
  IslandDetailResponse,
  TopIslandsResponse,
  TopSort,
  WindowDays,
} from './types';

const HOUR = 60 * 60_000;

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: HOUR,
        gcTime: 6 * HOUR,
        refetchOnWindowFocus: false,
        // A 4xx will not fix itself on retry (and a missing Phase 4–5 route must show "not available" at once).
        retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
      },
    },
  });
}

export const LAST_GOOD_PREFIX = 'studio:last-good:';

function readLastGood<T>(path: string): T | undefined {
  try {
    const raw = window.localStorage.getItem(LAST_GOOD_PREFIX + path);
    return raw === null ? undefined : (JSON.parse(raw) as T);
  } catch {
    return undefined;
  }
}

function writeLastGood(path: string, value: unknown): void {
  try {
    window.localStorage.setItem(LAST_GOOD_PREFIX + path, JSON.stringify(value));
  } catch {
    // Storage full or blocked: the in-memory cache still serves this session.
  }
}

export interface ApiQueryResult<T> {
  data: T | undefined;
  error: Error | null;
  isLoading: boolean;
  isFetching: boolean;
  /** The request failed and `data` is the last good response (in memory or from a previous visit). */
  showingLastGood: boolean;
  refetch: () => void;
}

/**
 * GET wrapper: caches for an hour, and on failure keeps serving the last good response so screens can show the
 * "Couldn't reach the Studio API, showing the last good snapshot" banner instead of going blank.
 */
export function useApiQuery<T>(
  path: string | null,
  opts: { persist?: boolean } & Pick<UseQueryOptions<T>, 'refetchInterval'> = {},
): ApiQueryResult<T> {
  const persist = opts.persist ?? true;
  const q = useQuery<T>({
    queryKey: ['api', path],
    enabled: path !== null,
    queryFn: async ({ signal }) => {
      const value = await apiFetch<T>(path as string, { signal });
      if (persist) writeLastGood(path as string, value);
      return value;
    },
    refetchInterval: opts.refetchInterval,
  });
  const fallback =
    q.isError && q.data === undefined && persist && path !== null ? readLastGood<T>(path) : undefined;
  const data = q.data ?? fallback;
  return {
    data,
    error: q.error,
    isLoading: q.isLoading,
    isFetching: q.isFetching,
    showingLastGood: q.isError && data !== undefined,
    refetch: () => {
      void q.refetch();
    },
  };
}

function qs(params: Record<string, string | number | undefined | null>): string {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(params))
    if (v !== undefined && v !== null && v !== '') s.set(k, String(v));
  const out = s.toString();
  return out ? `?${out}` : '';
}

export const paths = {
  freshness: () => '/meta/freshness',
  top: (p: {
    window: WindowDays;
    sort: TopSort;
    tag?: string | null;
    genre?: string | null;
    limit: number;
  }) => `/islands/top${qs({ window: p.window, sort: p.sort, tag: p.tag, genre: p.genre, limit: p.limit })}`,
  breakouts: () => '/islands/breakouts',
  island: (code: string) => `/islands/${encodeURIComponent(code)}`,
  genres: (dimension: GenreDimension, window: WindowDays) => `/genres${qs({ dimension, window })}`,
  genre: (key: string, dimension: GenreDimension, window: WindowDays) =>
    `/genres/${encodeURIComponent(key)}${qs({ dimension, window })}`,
  settings: () => '/settings',
};

export const useFreshness = () => useApiQuery<FreshnessResponse>(paths.freshness());
export const useTopIslands = (p: Parameters<typeof paths.top>[0]) =>
  useApiQuery<TopIslandsResponse>(paths.top(p));
export const useBreakouts = () => useApiQuery<BreakoutsResponse>(paths.breakouts());
export const useIsland = (code: string) => useApiQuery<IslandDetailResponse>(paths.island(code));
export const useGenres = (dimension: GenreDimension, window: WindowDays) =>
  useApiQuery<GenresResponse>(paths.genres(dimension, window));
export const useGenre = (key: string | null, dimension: GenreDimension, window: WindowDays) =>
  useApiQuery<GenreDetailResponse>(key === null ? null : paths.genre(key, dimension, window));

export const useSettings = () => useApiQuery<Settings>(paths.settings());

/** PUT /settings `{ llm }`; the response is the new GET shape, written straight into the cache. */
export function useSaveLlmRouting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (llm: LlmRouting) => apiFetch<Settings>(paths.settings(), { method: 'PUT', body: { llm } }),
    onSuccess: (next) => {
      qc.setQueryData(['api', paths.settings()], next);
      writeLastGood(paths.settings(), next);
    },
  });
}
